/**
 * Managing the org structure itself — legal entities and departments. `lookup.contract.ts` only
 * READS these (for pickers/filters); this file is where they get created and edited. Split apart
 * deliberately: lookup's whole reason to exist is "cheap, unpaginated, no permission surprises for
 * an ordinary employee" (see its own header comment), which does not describe a write endpoint.
 *
 * Legal entity creation is restricted to `legal_entity:create` (org_owner only, not hr_admin) —
 * registering a new legal entity is a bigger structural/compliance decision (CLAUDE.md invariant 4:
 * a LegalEntity owns statutory rules, currency, fiscal calendar) than adding a department, which
 * `department:manage` (hr_admin and up) covers.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  countrySchema,
  currencySchema,
  errorBodySchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  timezoneSchema,
  uuidSchema,
} from '../schemas/common.js';
import { jurisdictionProblems } from '../lib/jurisdictions.js';
import { isValidGstin } from '../gst.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const departmentDetailSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  parentId: uuidSchema.nullable(),
  headEmploymentId: uuidSchema.nullable(),
  isActive: z.boolean(),
});
export type DepartmentDetail = z.infer<typeof departmentDetailSchema>;

export const postDetailSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  title: z.string(),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  /** The grade's code (mirrored from the catalogue), or legacy free text on an unlinked post. */
  gradeLevel: z.string().nullable(),
  /** The catalogue grade this post sits at (Organisation → Grades), or null. */
  gradeId: uuidSchema.nullable(),
  gradeName: z.string().nullable(),
  isActive: z.boolean(),
});
export type PostDetail = z.infer<typeof postDetailSchema>;

export const legalEntityDetailSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  country: z.string(),
  currency: z.string(),
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  weekStartDay: z.number().int().min(0).max(6),
  timezone: z.string(),
  stateCode: z.string().nullable(),
  city: z.string().nullable(),
  /** Registered address, printed on payslips, letters and F&F statements. */
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  postalCode: z.string().nullable(),
  /** The entity's own GSTIN (India). Expense ITC and travel invoices are addressed to it. */
  gstin: z.string().nullable(),
  /** Who signs letters and certificates for this company. */
  signatoryName: z.string().nullable(),
  signatoryDesignation: z.string().nullable(),
  /** Public path of the company's logo, or null. Set via `POST /organisation/legal-entities/:id/logo`. */
  logoUrl: z.string().nullable(),
  isActive: z.boolean(),
});
export type LegalEntityDetail = z.infer<typeof legalEntityDetailSchema>;

/**
 * The payroll statutory registrations, read-only here: they are edited in Payroll > Statutory
 * settings (the payroll module owns them). The PAN is encrypted, so only whether one is on file.
 */
export const legalEntityStatutorySchema = z.object({
  configured: z.boolean(),
  corporateIdType: z.string().nullable(),
  corporateId: z.string().nullable(),
  panOnFile: z.boolean(),
  tan: z.string().nullable(),
  pfEstablishmentCode: z.string().nullable(),
  esicCode: z.string().nullable(),
  ptStateCode: z.string().nullable(),
  ptRegistrationNumber: z.string().nullable(),
  lwfStateCode: z.string().nullable(),
  lwfRegistrationNumber: z.string().nullable(),
});
export type LegalEntityStatutory = z.infer<typeof legalEntityStatutorySchema>;

/** One legal entity as the admin screen shows it: the profile, statutory summary and what uses it. */
export const legalEntityProfileSchema = legalEntityDetailSchema.extend({
  statutory: legalEntityStatutorySchema,
  activeEmployeeCount: z.number().int(),
  activeLocationCount: z.number().int(),
  /** True once a payroll run exists: currency and fiscal-year start can no longer change. */
  payrollStarted: z.boolean(),
});
export type LegalEntityProfile = z.infer<typeof legalEntityProfileSchema>;

/**
 * A work location — what the admin UI calls a Branch. The model is `Location` because attendance
 * geofencing, holiday calendars, and employee assignments already key off it; "branch" is only the
 * word on the screen.
 */
export const locationDetailSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  legalEntityId: uuidSchema.nullable(),
  legalEntityName: z.string().nullable(),
  /** The branch head's EMPLOYMENT (the job), not their person — same convention as managers. */
  headEmploymentId: uuidSchema.nullable(),
  /** Resolved server-side, so rendering a list needs no request per row. */
  headName: z.string().nullable(),
  headEmployeeCode: z.string().nullable(),
  addressLine1: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullable(),
  postalCode: z.string().nullable(),
  /** ISO 3166-1 alpha-2. A plain string on the way OUT, so a row written before validation still renders. */
  country: z.string(),
  /** IANA zone. */
  timezone: z.string(),
  /** 0 = Sunday … 6 = Saturday. The location's standing weekly offs (Keka's "work week"). */
  weeklyOffDays: z.array(z.number().int().min(0).max(6)),
  /** The geofence centre. Null when unset, or when the caller cannot manage this location. */
  coordinates: z.object({ latitude: z.number(), longitude: z.number() }).nullable(),
  /** Null means attendance is not geofenced at this location (or the caller cannot manage it). */
  geofenceRadiusMetres: z.number().int().nullable(),
  /** Configured clock-in method requirements, enforced server-side by the punch endpoint. */
  requireGeofence: z.boolean(),
  requireSelfie: z.boolean(),
  /** "Only office WiFi can punch" — enforced server-side against the punch request's own IP. */
  requireIpAllowlist: z.boolean(),
  /** Empty for callers who cannot manage this location: office IP ranges are not for everyone. */
  ipAllowlistCidrs: z.array(z.string()),
  isActive: z.boolean(),
});
export type LocationDetail = z.infer<typeof locationDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const createDepartmentBodySchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  parentId: uuidSchema.nullable().optional(),
  headEmploymentId: uuidSchema.nullable().optional(),
});

export const updateDepartmentBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  parentId: uuidSchema.nullable().optional(),
  headEmploymentId: uuidSchema.nullable().optional(),
  isActive: z.boolean().optional(),
});

export const createPostBodySchema = z.object({
  code: z.string().trim().min(1).max(20),
  title: z.string().trim().min(1).max(200),
  departmentId: uuidSchema.nullable().optional(),
  locationId: uuidSchema.nullable().optional(),
  /** Preferred: a grade from the catalogue. The API stores its code in `gradeLevel` too. */
  gradeId: uuidSchema.nullable().optional(),
  /** Free text, kept for older callers: links the catalogue grade with the same code if there is one. */
  gradeLevel: z.string().trim().max(100).optional(),
});

export const updatePostBodySchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  departmentId: uuidSchema.nullable().optional(),
  locationId: uuidSchema.nullable().optional(),
  /** `null` clears the grade; omit to leave it unchanged. */
  gradeId: uuidSchema.nullable().optional(),
  gradeLevel: z.string().trim().max(100).nullable().optional(),
  isActive: z.boolean().optional(),
});

/** Upper-cased, then checked against the GSTIN format and its check digit. */
const gstinSchema = z
  .string()
  .trim()
  .transform((value) => value.toUpperCase())
  .refine(isValidGstin, 'Not a valid GSTIN (15 characters, and the last one is a check digit)');

/** Entity codes: letters, digits, dash and underscore. Stored upper-case. */
const entityCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, dashes or underscores');

export const createLegalEntityBodySchema = z
  .object({
    code: entityCodeSchema,
    name: z.string().trim().min(1).max(200),
    country: countrySchema,
    currency: currencySchema,
    /** Defaults by country when omitted (April in India, July in Australia, January elsewhere). */
    fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
    weekStartDay: z.number().int().min(0).max(6).default(1),
    timezone: timezoneSchema,
    stateCode: z.string().max(10).optional(),
    city: z.string().max(100).optional(),
    addressLine1: z.string().trim().max(300).optional(),
    addressLine2: z.string().trim().max(300).optional(),
    postalCode: z.string().trim().max(20).optional(),
    gstin: gstinSchema.optional(),
    signatoryName: z.string().trim().max(200).optional(),
    signatoryDesignation: z.string().trim().max(200).optional(),
  })
  .superRefine((body, ctx) => {
    // The registered state is optional for an entity (the payroll setup wizard creates entities
    // without one, and payroll keeps its own PT/LWF state); when given it must be a real one.
    for (const problem of jurisdictionProblems({
      country: body.country,
      timezone: body.timezone,
      stateCode: body.stateCode,
    })) {
      if (problem.field === 'stateCode' && !body.stateCode) continue;
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [problem.field], message: problem.message });
    }
    if (body.gstin && body.country !== 'IN') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['gstin'],
        message: 'A GSTIN applies only to an entity registered in India',
      });
    }
  });

/**
 * Country, currency and fiscal year can change until the entity's first payroll run (signup detects
 * the country, so the owner must be able to correct it); after that they are locked. Timezone and
 * state are checked against the country by the API after merging with the stored row.
 */
export const updateLegalEntityBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  country: countrySchema.optional(),
  currency: currencySchema.optional(),
  fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
  weekStartDay: z.number().int().min(0).max(6).optional(),
  timezone: timezoneSchema.optional(),
  stateCode: z.string().max(10).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  addressLine1: z.string().trim().max(300).nullable().optional(),
  addressLine2: z.string().trim().max(300).nullable().optional(),
  postalCode: z.string().trim().max(20).nullable().optional(),
  gstin: gstinSchema.nullable().optional(),
  signatoryName: z.string().trim().max(200).nullable().optional(),
  signatoryDesignation: z.string().trim().max(200).nullable().optional(),
  isActive: z.boolean().optional(),
});

const coordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

/**
 * At least 25 m: phone GPS is typically accurate to 5–20 m outdoors and worse indoors, so a tighter
 * fence would refuse punches from people standing inside the building. At most 20 km, which already
 * covers a large campus or plant.
 */
const geofenceRadiusSchema = z.number().int().min(25).max(20_000);

/** A single IPv4/IPv6 address or CIDR block ("203.0.113.5" or "203.0.113.0/24"). Real reachability
 * (does this actually parse as an IP, is the prefix length sane) is re-checked server-side by
 * `isValidCidr` — this regex only keeps obviously-wrong input from reaching the request at all. */
const cidrPattern = /^([0-9a-fA-F:.]+)(\/\d{1,3})?$/;
const ipAllowlistCidrsSchema = z
  .array(z.string().trim().min(1).max(64).regex(cidrPattern, 'Not a valid IP address or CIDR block'))
  .max(20);

/** Blank optional text arrives from forms as ''; store it as null, not as an empty value. */
/** Distinct days of the week, 0 = Sunday. At most six: a location must have a working day. */
const weeklyOffDaysSchema = z
  .array(z.number().int().min(0).max(6))
  .max(6)
  .refine((days) => new Set(days).size === days.length, 'Each day can appear only once');

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();

export const listLocationsQuerySchema = paginationQuerySchema.extend({
  /** Matches code, name, or city. */
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('all'),
});

export const createLocationBodySchema = z
  .object({
    code: z.string().trim().min(1).max(20),
    name: z.string().trim().min(1).max(200),
    legalEntityId: uuidSchema.optional(),
    headEmploymentId: uuidSchema.optional(),
    addressLine1: optionalText(300),
    city: optionalText(100),
    stateCode: optionalText(50),
    postalCode: optionalText(20),
    country: countrySchema,
    timezone: timezoneSchema,
    coordinates: coordinatesSchema.optional(),
    geofenceRadiusMetres: geofenceRadiusSchema.optional(),
    requireGeofence: z.boolean().optional(),
    requireSelfie: z.boolean().optional(),
    requireIpAllowlist: z.boolean().optional(),
    ipAllowlistCidrs: ipAllowlistCidrsSchema.optional(),
    weeklyOffDays: weeklyOffDaysSchema.optional(),
  })
  .superRefine((body, ctx) => {
    const { country, timezone, stateCode } = body;
    for (const problem of jurisdictionProblems({ country, timezone, stateCode })) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [problem.field],
        message: problem.message,
      });
    }
    if (body.geofenceRadiusMetres !== undefined && !body.coordinates) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['geofenceRadiusMetres'],
        message: 'A geofence needs coordinates to measure from',
      });
    }
    if (body.requireGeofence && !body.coordinates) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['requireGeofence'],
        message: 'Requiring geofence verification needs coordinates to measure from',
      });
    }
    if (body.requireIpAllowlist && !(body.ipAllowlistCidrs && body.ipAllowlistCidrs.length > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['requireIpAllowlist'],
        message: 'Requiring an IP allow-list needs at least one address or CIDR block',
      });
    }
  });

/**
 * Every field optional; `null` clears a nullable one. Country, timezone, and state are validated
 * together by the API AFTER merging with the stored row — a PATCH changing only the timezone must
 * still be checked against the country already saved, which a body-only schema cannot see. The code
 * is immutable once created, like a department's.
 */
export const updateLocationBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  legalEntityId: uuidSchema.nullable().optional(),
  headEmploymentId: uuidSchema.nullable().optional(),
  addressLine1: z.string().trim().max(300).nullable().optional(),
  city: z.string().trim().max(100).nullable().optional(),
  stateCode: z.string().trim().max(50).nullable().optional(),
  postalCode: z.string().trim().max(20).nullable().optional(),
  country: countrySchema.optional(),
  timezone: timezoneSchema.optional(),
  coordinates: coordinatesSchema.nullable().optional(),
  geofenceRadiusMetres: geofenceRadiusSchema.nullable().optional(),
  requireGeofence: z.boolean().optional(),
  requireSelfie: z.boolean().optional(),
  requireIpAllowlist: z.boolean().optional(),
  ipAllowlistCidrs: ipAllowlistCidrsSchema.optional(),
  weeklyOffDays: weeklyOffDaysSchema.optional(),
  isActive: z.boolean().optional(),
});

/**
 * Someone whose current manager has left (exited, rescinded or archived). Keka asks HR to reassign
 * reportees when a manager exits; this is the list that question works from.
 */
export const orphanedReportSchema = z.object({
  employmentId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  departmentName: z.string().nullable(),
  managerEmploymentId: uuidSchema,
  managerName: z.string(),
  managerEmployeeCode: z.string(),
  /** Why the manager no longer counts: their employment status, or "archived". */
  managerStatus: z.string(),
});
export type OrphanedReport = z.infer<typeof orphanedReportSchema>;

/** Delete routes only ever succeed or refuse, so there is nothing to return beyond confirmation. */
const deleteResultSchema = z.object({ success: z.boolean() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const organisationContract = c.router(
  {
    createDepartment: {
      method: 'POST',
      path: '/departments',
      body: createDepartmentBodySchema,
      responses: {
        201: departmentDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a department',
      metadata: routeMeta('department:manage'),
    },
    updateDepartment: {
      method: 'PATCH',
      path: '/departments/:departmentId',
      body: updateDepartmentBodySchema,
      responses: {
        200: departmentDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a department — deactivation refused while people or sub-departments use it',
      metadata: routeMeta('department:manage'),
    },

    deleteDepartment: {
      method: 'DELETE',
      path: '/departments/:departmentId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Delete a department — refused if anything ever referenced it (history, budgets, policies); mark it inactive instead',
      metadata: routeMeta('department:manage'),
    },

    createPost: {
      method: 'POST',
      path: '/posts',
      body: createPostBodySchema,
      responses: {
        201: postDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a post / designation',
      metadata: routeMeta('post:manage'),
    },
    updatePost: {
      method: 'PATCH',
      path: '/posts/:postId',
      body: updatePostBodySchema,
      responses: {
        200: postDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a post / designation',
      metadata: routeMeta('post:manage'),
    },

    deletePost: {
      method: 'DELETE',
      path: '/posts/:postId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a post / designation',
      metadata: routeMeta('post:manage'),
    },

    listLegalEntityProfiles: {
      method: 'GET',
      path: '/legal-entities',
      responses: {
        200: z.object({ items: z.array(legalEntityProfileSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Legal entities with address, GSTIN, signatory, payroll statutory summary and usage (the admin company profile)',
      metadata: routeMeta('org.settings:read'),
    },

    createLegalEntity: {
      method: 'POST',
      path: '/legal-entities',
      body: createLegalEntityBodySchema,
      responses: {
        201: legalEntityDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Register a new legal entity',
      metadata: routeMeta('legal_entity:create'),
    },
    updateLegalEntity: {
      method: 'PATCH',
      path: '/legal-entities/:legalEntityId',
      body: updateLegalEntityBodySchema,
      responses: {
        200: legalEntityDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Update a legal entity — currency and fiscal year lock after its first payroll run; deactivation refused while people work there',
      metadata: routeMeta('legal_entity:update'),
    },

    listLocations: {
      method: 'GET',
      path: '/locations',
      query: listLocationsQuerySchema,
      responses: {
        200: paginatedSchema(locationDetailSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List work locations (branches) with their head and geofence',
      metadata: routeMeta('location:read'),
    },
    createLocation: {
      method: 'POST',
      path: '/locations',
      body: createLocationBodySchema,
      responses: {
        201: locationDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a work location (branch)',
      metadata: routeMeta('location:manage'),
    },
    updateLocation: {
      method: 'PATCH',
      path: '/locations/:locationId',
      body: updateLocationBodySchema,
      responses: {
        200: locationDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a work location (branch)',
      metadata: routeMeta('location:manage'),
    },

    listOrphanedReports: {
      method: 'GET',
      path: '/orphaned-reports',
      responses: {
        200: z.object({ items: z.array(orphanedReportSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Current employees whose manager has left — the people to reassign (each moved with employees.reassign)',
      metadata: routeMeta('employment:update'),
    },

    deleteLocation: {
      method: 'DELETE',
      path: '/locations/:locationId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Delete a location — refused if anything ever referenced it (history, attendance, holidays); mark it inactive instead',
      metadata: routeMeta('location:manage'),
    },
  },
  { pathPrefix: '/organisation' },
);
